import { useEffect, useState } from 'react';
import { createRoute, stripSearchParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';

import { rootRoute, useAppRouterInitial } from '../routerBase';
import { getOrderSource, orderSourcesQueryKeys } from '../../api/orderSources';
import {
  ORDER_SOURCES_SEARCH_DEFAULTS,
  parseOrderSourcesSearch,
} from '../../hooks/orderSources/useOrderSourceRouterSearch';
import { TableViewSkeleton } from '../../components/Skeleton/TableViewSkeleton';
import { DetailsSkeleton } from '../../components/Skeleton/DetailsSkeleton';
import { EditSkeleton } from '../../components/Skeleton/EditSkeleton';
import { useTranslation } from '../../hooks/useTranslation';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { CAPABILITIES } from '../../lib/capabilities';
import { requireCapability } from '../requireCapability';
// Type-only imports so the per-route bundles stay split — the runtime modules
// are loaded via dynamic import() inside each route's effect (see below).
import type OrderSourceManagement from '../../components/OrderSourceManagement/index';
import type OrderSourceDetails from '../../components/OrderSourceDetails/index';
import type EditOrderSource from '../../components/EditOrderSource/index';
import type { OrderSource } from '../../api/orderSources/types';
import type { OrderSourceTranslations } from '../../components/OrderSource/types';

export const ORDER_SOURCE_STALE_TIME_MS = 30_000;

export function useOrderSourceRaw(idNum: number, seed: OrderSource | undefined) {
  return useQuery({
    queryKey: orderSourcesQueryKeys.detail(idNum),
    queryFn: () => getOrderSource(idNum),
    staleTime: ORDER_SOURCE_STALE_TIME_MS,
    ...(seed
      ? { initialData: { order_source: seed }, initialDataUpdatedAt: Date.now() }
      : {}),
  });
}

export const orderSourcesIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/order_sources',
  validateSearch: parseOrderSourcesSearch,
  search: { middlewares: [stripSearchParams(ORDER_SOURCES_SEARCH_DEFAULTS)] },
  component: function OrderSourcesIndexRoute() {
    const { translations, currentUser } = useAppRouterInitial();
    const { t } = useTranslation();
    useDocumentTitle(t('common.page_titles.order_sources'));
    const [Component, setComponent] = useState<typeof OrderSourceManagement | null>(null);
    useEffect(() => {
      import('../../components/OrderSourceManagement/index').then((mod) => {
        setComponent(() => mod.default);
      });
    }, []);
    if (!Component) {
      return <TableViewSkeleton label={t('header.title')} />;
    }
    return (
      <Component
        translations={translations as OrderSourceTranslations | undefined}
        currentUser={currentUser}
      />
    );
  },
});

export const orderSourceDetailsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/order_sources/$id',
  component: function OrderSourceDetailsRoute() {
    const params = orderSourceDetailsRoute.useParams();
    const { initialOrderSource, currentUser } = useAppRouterInitial();
    const { t } = useTranslation();
    const idNum = Number(params.id);
    const seed =
      initialOrderSource && initialOrderSource.id === idNum ? initialOrderSource : undefined;
    const { data } = useOrderSourceRaw(idNum, seed);
    useDocumentTitle(data?.order_source?.name ?? t('common.page_titles.order_sources'));
    const [Component, setComponent] = useState<typeof OrderSourceDetails | null>(null);
    useEffect(() => {
      import('../../components/OrderSourceDetails/index').then((mod) => {
        setComponent(() => mod.default);
      });
    }, []);
    const orderSource = data?.order_source;
    if (!Component || !orderSource) {
      return <DetailsSkeleton label={t('header.title')} />;
    }
    return (
      <Component
        orderSourceId={idNum}
        initialOrderSource={orderSource}
        currentUser={currentUser}
      />
    );
  },
});

export const orderSourceEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/order_sources/$id/edit',
  // RBAC: edit is a write-only surface. The guard redirects read-only users
  // (no order_sources.write) to the read-only details page before the component
  // or its loader runs. The server policy enforces this too.
  beforeLoad: requireCapability(CAPABILITIES.ORDER_SOURCES_WRITE, ({ params }) => ({
    to: '/order_sources/$id',
    params: { id: params.id },
  })),
  component: function OrderSourceEditRoute() {
    const params = orderSourceEditRoute.useParams();
    const { initialOrderSource } = useAppRouterInitial();
    const { t } = useTranslation();
    const idNum = Number(params.id);
    const seed =
      initialOrderSource && initialOrderSource.id === idNum ? initialOrderSource : undefined;
    const { data } = useOrderSourceRaw(idNum, seed);
    useDocumentTitle(
      data?.order_source ? t('common.page_titles.edit', { name: data?.order_source.name }) : t('common.page_titles.order_sources'),
    );
    const [Component, setComponent] = useState<typeof EditOrderSource | null>(null);
    useEffect(() => {
      import('../../components/EditOrderSource/index').then((mod) => {
        setComponent(() => mod.default);
      });
    }, []);
    const orderSource = data?.order_source;
    if (!Component || !orderSource) {
      return <EditSkeleton label={t('header.title')} />;
    }
    return <Component orderSourceId={idNum} initialOrderSource={orderSource} />;
  },
});
